import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { useEffect, useRef } from 'react'
import { createElementFromHTML } from 'shared/lib/createElementFromHTML'
import { showToast } from 'shared/ui'
import { IconButton } from 'shared/ui/IconButton'
import { isMac } from 'shared/lib/deviceDetector'
import { getAriaLabel, logger } from 'shared/lib'
import { useShortenLink } from 'entities/ShortLink'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { textToHtml } from 'entities/Message/lib/textToHtml'
import { parseMentionsTemplate, replaceMentionTagsWithSpans } from 'entities/Message/lib/parseNote'
import {
  extractMarkdownImages,
  getCaretIndex,
  getClipboardMediaFiles,
  getHtmlClipboardImages,
  placeCaretAtEnd,
  placeCaretAtStart,
  removeImageAltLines,
} from 'widgets/MessageField/lib'
import { useMessageFieldContext } from 'widgets/MessageField/context/MessageFieldContext'
import { useMergeFields, useMergeFieldsCount } from 'widgets/MergeField'
import { Placeholder } from './Placeholder'
import styles from './styles.module.scss'

type ITextFieldProps = {
  isCollapse?: boolean
  className?: string
  classNamePlaceholder?: string
}

export const TextField = observer(
  ({ isCollapse, className, classNamePlaceholder }: ITextFieldProps) => {
    const entities = useEntitiesContext()
    const { shortLinkStore } = entities
    const messageFieldStore = useMessageFieldContext()
    const {
      inputActions,
      messageInnerText,
      replaceTriggerContent,
      updateTriggerContent,
      messageHtml,
      disablesSend,
      disabled,
      focusMessageFieldTrigger,
      blurMessageFieldTrigger,
      setMergeFieldsCount,
      setFocusMessageFieldTrigger,
      setMessageText,
      setAddContentAction,
      setReplaceContentFn,
      setReplaceSelectionAction,
      sendActionBtnProps,
      setUpdateTriggerContent,
      setSelectedContent,
      mentionsStore,
      isMarketing,
      setTextFieldElement,
    } = messageFieldStore
    const ref = useRef<HTMLDivElement>(null)
    const refInputKey = useRef('')
    const offsetRef = useRef(0)
    const focusNodeRef = useRef<Node | null>(null)
    const selectedRange = useRef<Range | null>(null)

    const updateCaretPosition = () => {
      if (ref.current) {
        const { offset, focusNode } = getCaretIndex(ref.current)
        offsetRef.current = offset
        focusNodeRef.current = !ref.current.innerHTML ? null : focusNode || null
      }
    }

    const addContent = (content: string, replaceContent?: string) => {
      if (!content || disabled) return

      const node = createElementFromHTML(content, true)
      new Set(node.querySelectorAll('[data-readonly]')).forEach((element) => {
        element.removeAttribute('data-readonly')
        element.setAttribute('contenteditable', 'false')
      })

      let focusNodeLocal = focusNodeRef.current
      let offsetLocal = offsetRef.current

      if (ref.current) {
        if (!focusNodeLocal) {
          ref.current.focus()
          const { focusNode, offset } = getCaretIndex(ref.current)

          focusNodeLocal = focusNode || ref.current || null
          offsetLocal = offset || 0
        }
        if (focusNodeLocal) {
          if (focusNodeLocal.nodeName === '#text') {
            if (focusNodeLocal.textContent) {
              const firstPart = focusNodeLocal.textContent.slice(
                0,
                offsetLocal - (replaceContent?.length || 0)
              )
              const firstNode = createElementFromHTML(firstPart, true)
              const secondPart = focusNodeLocal.textContent.slice(offsetLocal)
              const secondNode = createElementFromHTML(secondPart, true)
              const newNode = document.createElement('span')

              if (firstPart) {
                newNode.appendChild(firstNode)
              }
              newNode.appendChild(node)
              if (secondPart) {
                newNode.appendChild(secondNode)
              }
              if (newNode) {
                const parentNode = focusNodeLocal.parentNode
                if (parentNode) {
                  parentNode.replaceChild(newNode, focusNodeLocal)
                }
              }
              if (ref.current) {
                setMessageText(ref.current.innerHTML, ref.current.innerText)
                try {
                  if (secondPart) {
                    placeCaretAtStart(secondNode)
                  } else {
                    placeCaretAtEnd(newNode)
                  }
                } catch (e) {
                  logger.error(e)
                }
              }
            } else {
              const parentNode = focusNodeLocal.parentNode
              const newNode = createElementFromHTML(content, true)

              if (parentNode) {
                parentNode.replaceChild(newNode, focusNodeLocal)
              }
              setMessageText(ref.current.innerHTML, ref.current.innerText)
              if (focusNodeLocal) {
                placeCaretAtEnd(focusNodeLocal)
              } else {
                placeCaretAtEnd(ref.current)
              }
            }
          } else {
            const refChild = focusNodeLocal.childNodes[offsetLocal] || null
            focusNodeLocal.insertBefore(node, refChild)

            setMessageText(ref.current.innerHTML, ref.current.innerText)
            placeCaretAtEnd(node)
          }

          const { focusNode } = getCaretIndex(ref.current)

          const testFocusNodeLocal = focusNode || null
          if (!testFocusNodeLocal) {
            placeCaretAtEnd(ref.current)
          }
        }
      }
      updateCaretPosition()
      messageFieldStore.setUpdateTriggerContent()
    }

    const { onHideMergeFieldsTooltips, mergeFieldTooltipComponent } = useMergeFields({
      trigger: ref.current,
      updateTriggerDeps: [ref.current, updateTriggerContent, replaceTriggerContent],
      withEdit: !messageFieldStore.disableMergeFieldsFallback,
      setUpdateTriggerContent,
    })
    const { onHideLinkTooltips, linkTooltipComponent } = useShortenLink({
      trigger: ref.current,
      updateTriggerDeps: [ref.current, updateTriggerContent, replaceTriggerContent],
      setUpdateTriggerContent,
      addContent,
      isMarketing,
    })

    const onFocus = () => {
      if (disabled) return
      messageFieldStore.setActiveMessageField(true)
      updateCaretPosition()
      messageFieldStore.onFocus?.()
      shortLinkStore.setIsMarketing(false)
      shortLinkStore.setMessageText(messageInnerText)
    }

    const onClick = () => {
      if (ref.current) {
        messageFieldStore.onClick?.()
        mentionsStore.checkMessageKey(selectedRange.current)
      }
    }

    const onBlur = () => {
      if (!messageFieldStore.isVariantNote) {
        messageFieldStore.setActiveMessageField(false)
      }
      updateCaretPosition()
    }

    const replaceSelectedContent = (content: string) => {
      selectedRange.current?.startContainer &&
        placeCaretAtStart(selectedRange.current?.startContainer, selectedRange.current?.startOffset)
      updateCaretPosition()
      selectedRange.current?.deleteContents()

      addContent(content)
    }

    const replaceContent = (content: string, targetNode?: Node) => {
      if (!ref.current) return

      let focusNodeLocal: Node | null = targetNode ?? focusNodeRef.current

      if (!focusNodeLocal) {
        ref.current.focus()

        const { focusNode } = getCaretIndex(ref.current)

        focusNodeLocal = focusNode || ref.current || null
      }

      if (focusNodeLocal) {
        const newNode = createElementFromHTML(content, true)

        if (newNode) {
          const parentNode = focusNodeLocal.parentNode
          if (parentNode) {
            parentNode.replaceChild(newNode, focusNodeLocal)
          }
        }
        if (ref.current) {
          setMessageText(ref.current.innerHTML, ref.current.innerText)
          try {
            placeCaretAtEnd(newNode)
          } catch (e) {
            logger.error(e)
          }
        }
      }
    }

    const onPaste: React.ClipboardEventHandler<HTMLDivElement> = (e) => {
      if (disabled) return

      onClick()
      e.preventDefault()

      const { urls: markdownUrls, text: markdownText } = extractMarkdownImages(
        e.clipboardData.getData('text/plain')
      )
      let text = markdownText
      const files = getClipboardMediaFiles(e.clipboardData)

      if (files.length) {
        messageFieldStore.addMediaFiles(files)
      }

      if (markdownUrls.length) {
        messageFieldStore.addMediaFilesFromUrls(markdownUrls)
      }

      if (!files.length && !markdownUrls.length) {
        const {
          urls,
          files: htmlFiles,
          alts,
        } = getHtmlClipboardImages(e.clipboardData.getData('text/html'))

        if (urls.length) {
          messageFieldStore.addMediaFilesFromUrls(urls)
        }

        if (htmlFiles.length) {
          messageFieldStore.addMediaFiles(htmlFiles)
        }

        if (urls.length || htmlFiles.length) {
          text = removeImageAltLines(text, alts)
        }
      }

      if (!text) return

      const length = text.length
      const totalLength = messageInnerText.length + length
      if (
        ref.current &&
        messageFieldStore.textLimit.showNotification &&
        totalLength > messageFieldStore.textLimit.maxLength
      ) {
        ref.current.innerHTML = messageHtml
        placeCaretAtEnd(ref.current)

        showToast({
          type: 'error',
          title: messageFieldStore.textLimit.message,
        })
        return
      }

      window.getSelection()?.getRangeAt(0)?.deleteContents()
      updateCaretPosition()

      const supportsMentions =
        messageFieldStore.isVariantNote || messageFieldStore.isVariantConversation
      const processedText =
        supportsMentions && text.includes('@')
          ? parseMentionsTemplate(text, entities.usersStore.usersMap)
          : text
      const hasMentionTags = processedText !== text

      let html = textToHtml(entities, {
        text: processedText,
        extraSpace: true,
        ignoreLinks: messageFieldStore.disableLinks,
        isClearMergeField: messageFieldStore.replaceMergeFieldsOnPaste,
      })

      if (hasMentionTags) {
        html = replaceMentionTagsWithSpans(html, entities.usersStore.usersMap)
        if (!messageFieldStore.isModeNote) {
          messageFieldStore.setNoteMode(true)
        }
      }

      addContent(html)
    }

    const onInput = () => {
      if (disabled) return

      if (ref.current) {
        if (ref.current.innerText.includes('@') && messageFieldStore.isModeSms) {
          mentionsStore.checkMessageKey(selectedRange.current)
        }

        if (
          refInputKey.current !== 'Backspace' &&
          messageFieldStore.textLimit.showNotification &&
          ref.current.innerText.length > messageFieldStore.textLimit.maxLength
        ) {
          ref.current.innerHTML = messageHtml
          placeCaretAtEnd(ref.current)

          showToast({
            type: 'error',
            title: messageFieldStore.textLimit.message,
          })
          return
        }

        ref.current.querySelectorAll('a').forEach((link) => {
          const nextSibling = link.nextSibling

          if (
            !nextSibling ||
            nextSibling.nodeName === 'BR' ||
            nextSibling.textContent?.startsWith(' ')
          )
            return

          const textNode = document.createTextNode(' ')
          nextSibling.before(textNode)
        })

        if (ref.current.innerHTML === '<br>') {
          setMessageText('', '')
        } else {
          if (inputActions) {
            const inputAction = inputActions.get(ref.current?.innerText)

            if (inputAction) {
              inputAction.action()
              setMessageText('', '')
              ref.current.innerHTML = ''
              ref.current.innerText = ''

              return
            }
          }

          setMessageText(ref.current.innerHTML, ref.current.innerText)
        }
      }
    }

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (messageFieldStore.onKeyDown) {
        messageFieldStore.onKeyDown(e)
      }
      if (disabled) return
      if (refInputKey) refInputKey.current = e.code

      if ((e.ctrlKey || (isMac && e.metaKey)) && e.code === 'Enter' && !disablesSend && !disabled) {
        sendActionBtnProps?.onClick?.()
        setFocusMessageFieldTrigger()
      }
    }

    const onKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (messageFieldStore.onKeyUp) {
        messageFieldStore.onKeyUp(e)
      }
      if (ref.current) {
        mentionsStore.checkMessageKey(selectedRange.current)
      }
    }

    const onScroll = () => {
      onHideMergeFieldsTooltips()
      onHideLinkTooltips()
    }

    const onSelect = () => {
      if (!window.getSelection()?.rangeCount) return
      const range = window.getSelection()?.getRangeAt(0)
      setSelectedContent(range?.toString() || '')
      if (selectedRange && range) {
        selectedRange.current = range
      }
    }

    useMergeFieldsCount(ref, setMergeFieldsCount)

    useEffect(() => {
      shortLinkStore.init(false)
      messageFieldStore.onInit?.()

      return () => {
        shortLinkStore.reset()
      }
    }, [])

    useEffect(() => {
      if (ref.current && !messageInnerText) {
        ref.current.innerHTML = ''
        updateCaretPosition()
      }

      shortLinkStore.setIsMarketing(false)
      shortLinkStore.setMessageText(messageInnerText)
    }, [messageInnerText])

    useEffect(() => {
      if (ref.current && (replaceTriggerContent || messageHtml)) {
        ref.current.innerHTML = messageHtml
      }
    }, [replaceTriggerContent])

    useEffect(() => {
      if (ref.current && focusMessageFieldTrigger) {
        messageFieldStore.setActiveMessageField(true)
        placeCaretAtEnd(ref.current)
      }
    }, [focusMessageFieldTrigger, ref.current])

    useEffect(() => {
      if (ref.current && blurMessageFieldTrigger && messageFieldStore.activeMessageField) {
        messageFieldStore.setActiveMessageField(false)
        ref.current.blur()
      }
    }, [blurMessageFieldTrigger])

    useEffect(() => {
      setAddContentAction(addContent)
      setReplaceContentFn(replaceContent)
      setReplaceSelectionAction(replaceSelectedContent)
    }, [messageFieldStore])

    useEffect(() => {
      if (updateTriggerContent) {
        onInput()
      }
    }, [updateTriggerContent])

    useEffect(() => {
      setTextFieldElement(ref.current)
    }, [])

    return (
      <div
        className={classNames(
          styles.textareaWrapper,
          messageFieldStore.isVariantNote && styles.textareaNoteWrapper,
          {
            [styles.collapse]: isCollapse,
          }
        )}
      >
        {mergeFieldTooltipComponent}
        {linkTooltipComponent}
        <div className={styles.textareaContent}>
          <Placeholder className={classNamePlaceholder} />

          <div
            ref={ref}
            onPaste={onPaste}
            onKeyDown={onKeyDown}
            onKeyUp={onKeyUp}
            onInput={onInput}
            contentEditable={true}
            aria-label={getAriaLabel('TextInput', 'MessageField')}
            className={classNames(
              styles.textarea,
              {
                [styles.withContent]: messageHtml,
                [styles.active]: messageFieldStore.activeMessageField,
                [styles.isNote]: messageFieldStore.isVariantNote || messageFieldStore.isModeNote,
              },
              className
            )}
            style={{
              ...messageFieldStore.styles,
              minHeight: isCollapse ? 28 : messageFieldStore.styles?.minHeight,
            }}
            onBlur={onBlur}
            onFocus={onFocus}
            onClick={onClick}
            onScroll={onScroll}
            onSelect={onSelect}
          />
        </div>
        {messageFieldStore.isVariantNote &&
          !messageFieldStore.activeMessageField &&
          !messageHtml && (
            <IconButton
              icon={'arrowUpCircle'}
              color={'quaternary'}
              variant={'icon'}
              disabled
              ariaLabel='TextField'
            />
          )}
      </div>
    )
  }
)
